import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Facebook, Instagram, Menu, X, Info, Building2, Quote } from "lucide-react";
import ConceptCTAPill from "@/components/ConceptCTAPill";

const INK = "#F5F3EF";
const DARK = "#141414";
const SAGE = "#9CB08A";
const NAME = "Gallinero Dental Center";
const FB = "https://www.facebook.com/GallineroDentalCenterPH/";
const IG = "https://www.instagram.com/gallinerodentalcenterph/";

const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-80px" }, transition: { duration: 0.6, ease: "easeOut" } };

export default function GallineroConcept() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const s = () => setScrolled(window.scrollY > 8);
    s(); window.addEventListener("scroll", s);
    return () => window.removeEventListener("scroll", s);
  }, []);
  const links = [["About", "#about"], ["Locations", "#locations"], ["Our Dentists", "#dentists"], ["Reviews", "#reviews"], ["Contact", "#visit"]];

  return (
    <div id="top" className="min-h-screen" style={{ background: INK, color: "#1A1A1A", fontFamily: "'Work Sans', sans-serif" }} data-testid="gallinero-concept-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@500;600;700&family=Work+Sans:wght@300;400;500;600&display=swap');
        .gdc-serif { font-family: 'Cormorant Garamond', serif; }
      `}</style>

      <div data-testid="dental-disclaimer" className="bg-black text-white text-center text-xs py-2 relative z-50">
        <span className="inline-flex items-center gap-2"><Info size={13} className="text-white/70" /> Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {NAME}.</span>
      </div>

      <header className={`sticky top-0 z-40 transition-shadow ${scrolled ? "shadow-2xl" : ""}`} style={{ background: DARK }}>
        <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <a href="#top" data-testid="gdc-logo" className="flex items-center gap-3 text-white">
            <span className="w-10 h-10 rounded-full bg-white flex items-center justify-center gdc-serif text-lg font-bold" style={{ color: DARK }}>G</span>
            <span className="gdc-serif text-2xl tracking-wide">Gallinero</span>
          </a>
          <div className="hidden lg:flex items-center gap-9">
            {links.map(([l, h]) => <a key={h} href={h} className="text-sm text-white/75 hover:text-white transition-colors tracking-wide">{l}</a>)}
          </div>
          <a href="#visit" data-testid="gdc-header-cta" className="hidden sm:inline-flex rounded-none px-6 py-2.5 text-sm font-medium tracking-wide text-black transition-transform hover:-translate-y-0.5" style={{ background: SAGE }}>Book a Visit</a>
          <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="gdc-menu-toggle" aria-label="Menu">{open ? <X size={24} /> : <Menu size={24} />}</button>
        </nav>
        {open && (
          <div className="lg:hidden px-5 pb-4 space-y-1" style={{ background: DARK }}>
            {links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 text-white/90">{l}</a>)}
          </div>
        )}
      </header>

      {/* HERO — full bleed real signage wall photo */}
      <section className="relative h-[92vh] flex items-center" data-testid="gdc-hero">
        <img src="/dental/gallinero-dental-concept/sign-close.jpg" alt={NAME} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(0,0,0,0.67) 0%, rgba(0,0,0,0.38) 48%, rgba(0,0,0,0.06) 82%), linear-gradient(0deg, rgba(0,0,0,.32), transparent 55%)" }} />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full">
          <motion.div {...fadeUp} className="max-w-2xl">
            <span className="text-xs font-medium uppercase tracking-[0.25em] text-white/60">Iloilo City</span>
            <h1 className="mt-5 text-6xl sm:text-7xl lg:text-8xl gdc-serif text-white leading-[1.02]">Gallinero<br />Dental Center</h1>
            <p className="mt-6 text-lg text-white/80 font-light max-w-lg">Compassionate, professional dental care from a dedicated dental team — trusted across two locations in Iloilo.</p>
            <div className="mt-9 flex flex-wrap gap-4">
              <a href="#visit" data-testid="gdc-hero-book" className="px-8 py-3.5 text-sm font-medium tracking-wide text-black" style={{ background: SAGE }}>Book a Visit</a>
              <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="gdc-hero-message" className="px-8 py-3.5 text-sm font-medium tracking-wide text-white border border-white/40">Message Us</a>
            </div>
            <p className="mt-8 text-sm text-white/60" data-testid="gdc-trust-signal">4.9★ Rating · 234 Reviews · Two Locations in Iloilo</p>
          </motion.div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="max-w-5xl mx-auto px-5 sm:px-8 py-24 text-center">
        <motion.p {...fadeUp} className="text-2xl sm:text-3xl gdc-serif leading-relaxed" style={{ color: "#2A2A2A" }}>
          Families across Iloilo trust Gallinero Dental Center for welcoming, gentle treatment — delivered with genuine care by Dr. Jerah Leen Mai V. Gallinero, Dr. Judah V. Gallinero, and their team.
        </motion.p>
      </section>

      {/* DENTISTS SPOTLIGHT */}
      <section id="dentists" className="max-w-7xl mx-auto px-5 sm:px-8 pb-24 grid md:grid-cols-2 gap-4">
        <motion.div {...fadeUp} className="relative">
          <img src="/dental/gallinero-dental-concept/team-doctor.jpg" alt="Dr. Jerah Leen Mai V. Gallinero & Dr. Judah V. Gallinero" className="w-full h-[520px] object-cover" style={{ objectPosition: "75% center" }} />
        </motion.div>
        <motion.div {...fadeUp} className="flex flex-col items-start justify-center p-8 md:p-12" style={{ background: DARK }}>
          <span className="text-xs uppercase tracking-[0.25em]" style={{ color: SAGE }}>Our Dentists</span>
          <h2 className="mt-3 text-4xl gdc-serif text-white leading-snug">Dr. Jerah Leen Mai V. Gallinero &amp;<br />Dr. Judah V. Gallinero</h2>
          <p className="mt-5 text-white/70 font-light leading-relaxed">A dedicated dental team known across Iloilo for welcoming, gentle treatment, spotless facilities, and care that goes beyond the chair.</p>
        </motion.div>
      </section>

      {/* TEAM */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <motion.div {...fadeUp} className="grid md:grid-cols-[1fr,1.4fr] gap-10 items-center">
          <div>
            <span className="text-xs uppercase tracking-[0.25em]" style={{ color: "#6B8256" }}>Our Team</span>
            <h2 className="mt-3 text-4xl gdc-serif">More than one pair of hands</h2>
            <p className="mt-4 text-[#3A3A3A]/80 font-light leading-relaxed">Behind every visit is a full team dedicated to making patients feel at home the moment they walk in.</p>
          </div>
          <img src="/dental/gallinero-dental-concept/team-staff.jpg" alt="The Gallinero Dental Center team" className="w-full h-96 object-cover" />
        </motion.div>
      </section>

      {/* LOCATIONS */}
      <section id="locations" className="py-24" style={{ background: DARK }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp} className="mb-14">
            <span className="text-xs uppercase tracking-[0.25em]" style={{ color: SAGE }}>Two Locations</span>
            <h2 className="mt-3 text-4xl sm:text-5xl gdc-serif text-white">Find Us in Iloilo</h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 gap-6">
            {[
              { name: "Jibao-an, Pavia (Main)", note: "Gallinero 2020 Building, Zone 4, Jibao-an, Pavia, Iloilo" },
              { name: "Second Branch", note: "Maria Luisa Building / PHHC Road area, Iloilo" },
            ].map((b) => (
              <motion.div key={b.name} {...fadeUp} data-testid="gdc-branch" className="p-8 border border-white/15">
                <Building2 size={20} style={{ color: SAGE }} />
                <h3 className="mt-4 text-xl text-white gdc-serif">{b.name}</h3>
                <p className="mt-2 text-white/60 text-sm font-light">{b.note}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* REAL RESULTS */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-24">
        <motion.div {...fadeUp} className="text-center mb-10">
          <span className="text-xs uppercase tracking-[0.25em]" style={{ color: "#6B8256" }}>Real Results</span>
          <h2 className="mt-3 text-4xl gdc-serif">A Smile We're Proud Of</h2>
        </motion.div>
        <motion.img {...fadeUp} src="/dental/gallinero-dental-concept/before-after.jpg" alt="A smile result shared by Gallinero Dental Center" className="w-full max-w-sm mx-auto object-contain" />
      </section>

      {/* TESTIMONIALS */}
      <section id="reviews" className="py-24" style={{ background: "#EFEDE6" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <span className="text-xs uppercase tracking-[0.25em]" style={{ color: "#6B8256" }}>234 Reviews · 4.9★</span>
            <h2 className="mt-3 text-4xl sm:text-5xl gdc-serif">What Patients Are Saying</h2>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-10">
            {[
              { text: "Excellent experience with Dr. Jerah, Dr. Judah and staff — welcoming, gentle treatment, cleanliness and oral care.", author: "S." },
              { text: "I always feel comfortable at Gallinero — Dr. Jerah and Dr. Judah are wonderful.", author: "Kim Jay Ann Halongong" },
              { text: "My trusted dental center in Iloilo — highly recommend the clinic.", author: "Ilonggo Kuya Hermz" },
              { text: "Painless and quick — professional and compassionate.", author: "Christian Bernie Tirol" },
            ].map((q, i) => (
              <motion.div key={i} {...fadeUp} data-testid={`gdc-testimonial-${i}`} className="flex gap-4">
                <Quote size={26} style={{ color: SAGE }} className="shrink-0 mt-1" />
                <div>
                  <p className="text-[#2A2A2A]/85 font-light leading-relaxed">"{q.text}"</p>
                  <p className="mt-2 text-sm font-semibold" style={{ color: "#6B8256" }}>{q.author}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 text-center" style={{ background: DARK }}>
        <h2 className="text-3xl sm:text-4xl gdc-serif text-white">Ready to visit Gallinero?</h2>
        <div className="mt-8 flex flex-wrap justify-center gap-4">
          <a href="tel:+639063364854" data-testid="gdc-cta-call" className="px-8 py-3.5 text-sm font-medium text-black" style={{ background: SAGE }}>Call the Clinic</a>
          <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="gdc-cta-message" className="px-8 py-3.5 text-sm font-medium text-white border border-white/40">Message on Facebook</a>
        </div>
      </section>

      {/* VISIT */}
      <section id="visit" className="max-w-7xl mx-auto px-5 sm:px-8 py-24 grid md:grid-cols-2 gap-12">
        <motion.div {...fadeUp}>
          <span className="text-xs uppercase tracking-[0.25em]" style={{ color: "#6B8256" }}>Visit Us</span>
          <h2 className="mt-3 text-4xl gdc-serif">Get in Touch</h2>
          <ul className="mt-8 space-y-6">
            <li className="flex items-start gap-4"><MapPin size={20} style={{ color: "#6B8256" }} className="mt-0.5" /><div><div className="text-xs uppercase text-[#5C564D]">Address</div><div>Iloilo City–Aleosan Rd, Jibao-an, Pavia, Iloilo</div></div></li>
            <li className="flex items-start gap-4"><Phone size={20} style={{ color: "#6B8256" }} className="mt-0.5" /><div><div className="text-xs uppercase text-[#5C564D]">Phone</div><a href="tel:+639063364854" className="hover:underline">+63 906 336 4854</a></div></li>
            <li className="flex items-start gap-4"><Facebook size={20} style={{ color: "#6B8256" }} className="mt-0.5" /><div><div className="text-xs uppercase text-[#5C564D]">Facebook</div><a href={FB} target="_blank" rel="noopener noreferrer" data-testid="gdc-facebook-link" className="hover:underline">GallineroDentalCenterPH</a></div></li>
            <li className="flex items-start gap-4"><Instagram size={20} style={{ color: "#6B8256" }} className="mt-0.5" /><div><div className="text-xs uppercase text-[#5C564D]">Instagram</div><a href={IG} target="_blank" rel="noopener noreferrer" data-testid="gdc-instagram-link" className="hover:underline">gallinerodentalcenterph</a></div></li>
          </ul>
        </motion.div>
        <motion.div {...fadeUp} className="min-h-[320px] border" style={{ borderColor: "#ddd" }}>
          <iframe title={`${NAME} map`} src={`https://www.google.com/maps?q=${encodeURIComponent("Iloilo City Aleosan Road Jibao-an Pavia Iloilo")}&output=embed`} className="w-full h-full min-h-[320px]" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
        </motion.div>
      </section>

      <footer className="py-12" style={{ background: DARK, color: "#FFFFFF80" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row justify-between gap-4 text-xs">
          <p className="gdc-serif text-white text-base">Gallinero Dental Center</p>
          <p>This is an unsolicited concept website by MySiteCare. Not operated by or affiliated with {NAME}. Confirm details directly with the clinic before booking.</p>
        </div>
      </footer>

      <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="dental-sticky-contact" className="lg:hidden fixed bottom-4 inset-x-4 z-50 px-6 py-3.5 text-center text-sm font-medium text-black shadow-xl" style={{ background: SAGE }}>Message Gallinero</a>
      <ConceptCTAPill slug="gallinero-dental-concept" />
    </div>
  );
}
